/*
 * <room-sizer> - the room view of the PDP eye preview (snippets/room-sizer.liquid, assets/pdp-eye.js): the design
 * hanging in a real room at the size and frame it is shown (show()), to scale against the furniture under it; the
 * painting glides from one size to the next, rooms crossfade.
 *
 * Which room, per size:
 *  - proportion: the furniture under the painting suits its size (the interior rule: art
 *    about 2/3 the furniture's width), so a 32x16 hangs over a sideboard and an 84x42 over a
 *    sectional - never a small piece lost above a big sofa;
 *  - colour: the catalog engine's own room rule (catalog-engine/src/rooms/selection.ts,
 *    COLOUR_PALETTES / NEUTRAL_PALETTES / paletteFits, copied here as they are): a room in one
 *    of the design's colours first, then a neutral one, never one whose palette fights the art;
 *  - neighbouring sizes keep a room while it still suits them, so the painting visibly grows.
 *
 * The rooms are approved rooms of the catalog's room library, as of its 6 Oct 2026 export
 * (photos in the store's Files, room-sizer-<SKU>.jpg; palettes and placement from that export). The
 * library records no real-world scale, so each room's is calibrated below from its furniture.
 */
(() => {
  if (window.customElements.get('room-sizer')) return;

  /* Room pool. px are in the photo's 2048 square.
     ppi       px per real inch on the wall, from the furniture (furnIn = its estimated width)
     cx        the furniture's centre; topY its top edge, which the art hangs above; floorY its foot
     wall      the clear wall it may use [left, top, right]; avoid: lamps, plants, vases in
               front of the wall, [x0, y0, x1, y1]
     palettes  the room's palettes in the library */
  const R = (ppi, furnIn, what, cx, topY, floorY, wall, palettes, avoid) => ({ ppi, furnIn, what, cx, topY, floorY, wall, palettes, avoid });
  const ROOMS = {
    // consoles, sideboards, dressers
    R0114: R(26, 60, 'sideboard', 1014, 954, 1994, [0, 0, 2048], ['Green']),
    R0116: R(35, 52, 'dresser', 1002, 1734, 2048, [0, 0, 2048], ['Pink', 'Pop of Color', 'Blue'], [[200, 1240, 1780, 1734]]),
    R0086: R(24, 75, 'sideboard', 1018, 1283, 1995, [0, 0, 2048], ['Light Neutral', 'B&W'], [[300, 1000, 620, 1290], [1625, 1055, 1855, 1290], [0, 870, 315, 1290]]),
    R0108: R(38, 48, 'console table', 1234, 1489, 2048, [0, 0, 2048], ['Light Neutral', 'Earthy'], [[1110, 1020, 1450, 1489], [460, 1320, 1060, 1489]]),
    R0233: R(19.4, 54, 'sideboard', 514, 1259, 1841, [0, 0, 1380], ['Light Neutral', 'Gray'], [[60, 1070, 340, 1259], [1380, 0, 2048, 1900]]),
    R0094: R(22.3, 65, 'sideboard', 1025, 1273, 1993, [0, 0, 2048], ['Light Neutral', 'Earthy', 'Blue'], [[380, 1060, 600, 1500], [1425, 780, 1720, 1273]]),
    R0104: R(30.1, 54, 'sideboard', 950, 1060, 1993, [0, 0, 1480], ['Light Neutral', 'Orange']),
    R0112: R(21, 61, 'dresser', 1017, 1199, 1910, [0, 0, 2048], ['Pop of Color', 'Earthy', 'Red'], [[500, 1030, 800, 1205]]),
    R0117: R(22.7, 65, 'sideboard', 1031, 1143, 1994, [0, 0, 2048], ['Earthy', 'Dark Neutral']),
    R0099: R(25.8, 73, 'media console', 1019, 1290, 1910, [0, 0, 2048], ['Earthy', 'Yellow'], [[170, 1140, 300, 1290]]),
    R0121: R(26, 80, 'media console', 1024, 1259, 1785, [0, 0, 2048], ['Gray', 'Blue', 'Light Neutral'], [[1780, 1070, 1890, 1259]]),
    // beds
    R0278: R(13.4, 60, 'queen bed', 1067, 1024, 1773, [465, 97, 1557], ['Light Neutral', 'Pink'], [[490, 820, 630, 1005], [1460, 820, 1590, 1005]]),
    R0294: R(17.5, 64, 'queen bed', 1024, 918, 2048, [144, 61, 1906], ['Blue'], [[160, 0, 520, 730], [1530, 0, 1890, 730]]),
    R0308: R(16.5, 70, 'bed', 980, 1020, 1800, [388, 0, 1551], ['Dark Neutral', 'B&W', 'Gray']),
    R0310: R(15.5, 78, 'king bed', 1022, 919, 1940, [0, 0, 2048], ['Blue', 'Gray'], [[30, 920, 320, 1340], [1640, 920, 1925, 1340]]),
    R0302: R(19.5, 80, 'king bed', 1038, 1018, 2048, [77, 111, 1961], ['Purple', 'Pop of Color'], [[380, 0, 445, 1110], [1660, 0, 1730, 1110]]),
    R0264: R(16.6, 80, 'king bed', 1024, 780, 1911, [0, 0, 1898], ['Light Neutral', 'Gray', 'Orange'], [[130, 610, 470, 1140]]),
    R0298: R(15.6, 62, 'queen bed', 1007, 1052, 1520, [0, 0, 2048], ['Light Neutral', 'Gray', 'B&W'], [[320, 1000, 470, 1300], [1550, 1000, 1710, 1300]]),
    R0275: R(16, 62, 'queen bed', 1024, 795, 1450, [304, 0, 1910], ['Blue'], [[245, 780, 450, 1400], [1655, 780, 1865, 1400]]),
    R0284: R(17, 62, 'queen bed', 1024, 977, 1600, [0, 0, 2048], ['Pink', 'Purple'], [[1600, 850, 1940, 1300]]),
    R0286: R(16, 62, 'queen bed', 1024, 811, 1500, [271, 0, 1772], ['Gray', 'Dark Neutral']),
    R0079: R(19.9, 64, 'queen bed', 1030, 1185, 1950, [22, 0, 2026], ['Earthy', 'Orange'], [[0, 840, 140, 1085], [1920, 840, 2048, 1085]]),
    R0074: R(20.7, 64, 'bed', 1121, 991, 1600, [0, 0, 1680], ['Green'], [[1650, 430, 2048, 1600]]),
    R0304: R(18.1, 66, 'queen bed', 1024, 913, 1500, [42, 78, 1910], ['Dark Neutral', 'Green'], [[190, 530, 325, 700], [1650, 530, 1775, 700]]),
    // sofas
    R0424: R(21, 56, 'loveseat', 851, 1196, 1911, [0, 0, 2048], ['Blue', 'Pop of Color', 'Yellow'], [[60, 890, 270, 1900], [1620, 960, 2048, 1640]]),
    R0155: R(16, 72, 'sofa', 761, 1219, 1911, [0, 0, 1350], ['Blue', 'Pop of Color'], [[0, 1080, 190, 1280]]),
    R0226: R(13.5, 80, 'sofa', 984, 1168, 1634, [0, 0, 2048], ['Pop of Color', 'Yellow'], [[100, 900, 440, 1210], [1570, 1110, 2048, 1340]]),
    R0457: R(15.5, 90, 'sofa', 990, 1108, 1780, [0, 0, 2048], ['Light Neutral', 'Green'], [[1500, 400, 2048, 1400]]),
    R0222: R(21, 90, 'sofa', 1028, 1031, 1662, [0, 0, 2048], ['Dark Neutral', 'Gray']),
    R0385: R(18.9, 66, 'loveseat', 1406, 1168, 1799, [0, 0, 2048], ['Pop of Color', 'Red'], [[0, 975, 530, 1168]]),
    R0427: R(17.6, 80, 'sofa', 1024, 1237, 1716, [0, 83, 2048], ['Yellow', 'Pink', 'Pop of Color'], [[80, 850, 350, 1030], [1680, 740, 2048, 1240]]),
    R0476: R(17.3, 84, 'sofa', 1024, 1210, 1710, [464, 124, 1584], ['Light Neutral', 'Pink']),
    R0338: R(19.5, 84, 'sofa', 1024, 1102, 1730, [0, 0, 2048], ['Blue'], [[75, 670, 230, 1500], [1735, 770, 2030, 1250]]),
    R0421: R(19.1, 84, 'sofa', 1024, 1057, 1716, [271, 0, 2048], ['Blue', 'Dark Neutral']),
    R0439: R(22, 84, 'sofa', 1032, 922, 1633, [0, 0, 2048], ['Green', 'Earthy', 'Dark Neutral']),
    R0448: R(19.4, 88, 'sofa', 906, 1080, 1619, [28, 0, 2048], ['Earthy', 'Dark Neutral'], [[1690, 670, 2048, 1080]]),
    R0157: R(22, 89, 'sofa', 1024, 900, 1675, [0, 0, 2048], ['Green', 'Dark Neutral'], [[1460, 810, 2000, 900]]),
    R0154: R(21.4, 90, 'sofa', 1038, 1052, 1702, [0, 0, 2048], ['Dark Neutral', 'B&W']),
    R0489: R(19, 91, 'sofa', 1071, 1143, 1661, [50, 0, 2048], ['Green', 'Earthy'], [[1790, 740, 2048, 1143]]),
    R0452: R(21.2, 92, 'sofa', 1038, 935, 1660, [0, 0, 2048], ['Light Neutral', 'Pink']),
    R0430: R(13.9, 95, 'sofa', 1372, 1090, 1550, [205, 0, 2048], ['Light Neutral', 'Pink']),
    R0395: R(18.4, 100, 'sofa', 1030, 1068, 1522, [249, 0, 1716], ['Orange', 'Pop of Color'], [[130, 810, 325, 990]]),
    // sectionals
    R0196: R(16, 115, 'sectional', 957, 1068, 1800, [0, 0, 2048], ['Light Neutral', 'Gray', 'Yellow'], [[1680, 690, 2010, 1860]]),
    R0188: R(15, 130, 'sectional', 994, 1154, 1760, [0, 0, 2048], ['Pop of Color', 'Red', 'Light Neutral'], [[1860, 680, 2048, 1800]]),
    R0407: R(20.7, 76, 'sectional', 1121, 1246, 1827, [0, 0, 2048], ['Pink', 'Pop of Color'], [[0, 570, 640, 1246]]),
    R0485: R(18, 110, 'sectional', 1024, 1218, 1689, [257, 0, 2048], ['Orange', 'Yellow', 'Pop of Color']),
    R0208: R(16.9, 110, 'sectional', 1010, 860, 1620, [0, 0, 2048], ['Blue', 'Gray', 'Dark Neutral']),
    R0444: R(17.8, 120, 'sectional', 1038, 1143, 1605, [0, 0, 2048], ['Pop of Color', 'Red']),
    R0469: R(14.7, 120, 'sectional', 977, 1107, 1461, [102, 199, 2043], ['Light Neutral', 'Gray'], [[240, 140, 300, 930], [1815, 140, 1875, 930], [1740, 1150, 2048, 1500]]),
    // a small armchair, and a tall wall for the 72in pieces
    R0234: R(19.7, 38, 'armchair', 1268, 1182, 1799, [158, 0, 1827], ['Red', 'Pop of Color'], [[250, 530, 640, 1800]]),
    R0372: R(9.7, 62, 'bench', 1341, 1232, 1460, [1060, 291, 2048], ['Earthy', 'Light Neutral', 'Pink'], [[1640, 1190, 2000, 1560]]),
  };

  /* The shapes each room is approved for in the catalog's room console, and where the console
     places a painting of that shape (the centre of its box - hand-tuned by the team in most
     rooms, else the console's own automatic placement, catalog-engine/src/rooms/placement.ts).
     The size guide hangs its true-size painting there, so it sits where the team placed it -
     between the pendants, clear of the window - and is the room the product's own room images
     use. A room is only used for the shapes listed. From the 6 Oct 2026 room export. */
  const SHAPES = {
    R0108: { '1:1': [1024, 605], '4:3': [1024, 588], '3:2': [1024, 584], '2:1': [1024, 614], '3:1': [1024, 730] },
    R0233: { '3:4': [1024, 640], '2:3': [1117, 662], '1:1': [1168, 684], '4:3': [1024, 642], '3:2': [1024, 652], '2:1': [1024, 614], '3:1': [1024, 731] },
    R0094: { '3:4': [1024, 632], '2:3': [1024, 644], '1:1': [1024, 618], '2:1': [1024, 510], '3:1': [1024, 568] },
    R0104: { '3:4': [1024, 538], '2:3': [1024, 540], '1:1': [1024, 540], '4:3': [1024, 494], '3:2': [1024, 494], '2:1': [1024, 570] },
    R0112: { '4:3': [1024, 588], '3:2': [1024, 580], '2:1': [1024, 662], '3:1': [1024, 670] },
    R0117: { '3:4': [1024, 568], '2:3': [1024, 572], '1:1': [1024, 577], '4:3': [1024, 582], '3:2': [1024, 599], '2:1': [1024, 570], '3:1': [1024, 570] },
    R0099: { '3:4': [1024, 661], '2:3': [1022, 631], '1:1': [1024, 605], '4:3': [1024, 645], '3:2': [1024, 652], '2:1': [1024, 646], '3:1': [1024, 646] },
    R0121: { '3:4': [1024, 688], '2:3': [1024, 636], '1:1': [1024, 688], '4:3': [1024, 655], '3:2': [1024, 622], '2:1': [1024, 625], '3:1': [1024, 686] },
    R0298: { '3:4': [1024, 534], '2:3': [1024, 527], '1:1': [1024, 541], '4:3': [1024, 521], '3:2': [1024, 546], '2:1': [1024, 526], '3:1': [1024, 626] },
    R0275: { '1:1': [1026, 410], '4:3': [1024, 404], '3:2': [1024, 404], '2:1': [1024, 418], '3:1': [1024, 498] },
    R0284: { '3:4': [1024, 500], '2:3': [1024, 504], '1:1': [1024, 486], '4:3': [1024, 472], '3:2': [1024, 493], '2:1': [1024, 524], '3:1': [1024, 609] },
    R0286: { '3:4': [1024, 418], '2:3': [1024, 405], '1:1': [1024, 406], '4:3': [1024, 411], '3:2': [1024, 407], '2:1': [1024, 457], '3:1': [1024, 528] },
    R0079: { '3:4': [1028, 600], '2:3': [1024, 596], '1:1': [1024, 604], '4:3': [1024, 617], '3:2': [1024, 628], '2:1': [1024, 594], '3:1': [1024, 698] },
    R0074: { '3:4': [1147, 493], '2:3': [1147, 489], '1:1': [1147, 493], '4:3': [1123, 492], '3:2': [1137, 503], '2:1': [1141, 527], '3:1': [1136, 628] },
    R0304: { '3:4': [980, 505], '2:3': [976, 501], '1:1': [976, 503], '4:3': [973, 502], '3:2': [979, 497], '2:1': [978, 553], '3:1': [977, 601] },
    R0385: { '3:4': [1024, 602], '2:3': [1024, 598], '1:1': [1024, 606], '4:3': [1076, 692], '3:2': [1191, 704], '2:1': [1024, 578], '3:1': [1024, 686] },
    R0427: { '3:4': [1024, 652], '2:3': [1024, 642], '1:1': [1024, 674], '4:3': [1024, 710], '3:2': [1024, 688], '2:1': [1024, 744], '3:1': [1024, 788] },
    R0476: { '3:4': [1024, 666], '2:3': [1024, 660], '1:1': [1024, 666], '4:3': [1024, 670], '3:2': [1024, 678], '2:1': [1024, 720], '3:1': [1022, 724] },
    R0338: { '3:4': [1024, 564], '2:3': [1024, 567], '1:1': [1024, 586], '4:3': [1024, 552], '3:2': [1024, 580], '2:1': [1024, 542], '3:1': [1024, 542] },
    R0421: { '3:4': [1024, 554], '2:3': [1024, 526], '1:1': [1024, 559], '4:3': [1024, 580], '3:2': [1024, 590], '2:1': [1024, 622], '3:1': [1024, 696] },
    R0439: { '3:4': [911, 467], '2:3': [896, 470], '1:1': [1024, 474], '4:3': [1024, 482], '3:2': [1024, 482], '2:1': [1024, 516], '3:1': [1024, 528] },
    R0448: { '3:4': [906, 564], '2:3': [906, 568], '1:1': [906, 634], '4:3': [915, 644], '3:2': [940, 656], '2:1': [1024, 540], '3:1': [1024, 654] },
    R0157: { '4:3': [1024, 458], '3:2': [1024, 444], '2:1': [1024, 448], '3:1': [1024, 468] },
    R0154: { '3:4': [1024, 528], '2:3': [1036, 528], '1:1': [1024, 534], '4:3': [1024, 532], '3:2': [1024, 545], '2:1': [1024, 526], '3:1': [1024, 570] },
    R0489: { '3:4': [1069, 567], '2:3': [1069, 584], '1:1': [1069, 584], '4:3': [1093, 574], '3:2': [1076, 588], '2:1': [1079, 596], '3:1': [1024, 694] },
    R0452: { '3:4': [1024, 486], '2:3': [1024, 504], '1:1': [1024, 478], '4:3': [1024, 472], '3:2': [1024, 472], '2:1': [1024, 472], '3:1': [1024, 532] },
    R0430: { '3:4': [1372, 622], '2:3': [1372, 608], '1:1': [1372, 713], '4:3': [1384, 720], '3:2': [1383, 729], '2:1': [1080, 688], '3:1': [1115, 788] },
    R0395: { '3:4': [1024, 569], '2:3': [1024, 598], '1:1': [1024, 599], '4:3': [1024, 636], '3:2': [1024, 632], '2:1': [972, 698], '3:1': [981, 752] },
    R0407: { '3:4': [1267, 736], '2:3': [1264, 670], '1:1': [1276, 766], '4:3': [1276, 810], '3:2': [1279, 837], '2:1': [1302, 910], '3:1': [1314, 946] },
    R0485: { '3:4': [1124, 710], '2:3': [1107, 732], '1:1': [1124, 738], '4:3': [1076, 752], '3:2': [1118, 778], '2:1': [1081, 813], '3:1': [1092, 905] },
    R0208: { '3:4': [1024, 446], '2:3': [1024, 436], '1:1': [1024, 432], '4:3': [1024, 440], '3:2': [1024, 440], '2:1': [1024, 436], '3:1': [1024, 482] },
    R0444: { '3:4': [1024, 584], '2:3': [1024, 574], '1:1': [1024, 592], '4:3': [1024, 572], '3:2': [1024, 590], '2:1': [1024, 610], '3:1': [1024, 696] },
    R0469: { '3:4': [1024, 644], '2:3': [1024, 644], '1:1': [1024, 665], '4:3': [1024, 650], '3:2': [1064, 688], '2:1': [1024, 686], '3:1': [1068, 688] },
    R0234: { '3:4': [1126, 610], '2:3': [1152, 620], '1:1': [1199, 622], '4:3': [1146, 662], '3:2': [1172, 630] },
    R0372: { '3:4': [1358, 780], '2:3': [1341, 802], '1:1': [1320, 786], '4:3': [1368, 780], '3:2': [1352, 800] },
    R0114: { '1:1': [1024, 485], '4:3': [1024, 475], '3:2': [1024, 487], '2:1': [1024, 487], '3:1': [1024, 477] },
    R0116: { '3:4': [1024, 748], '2:3': [1024, 726], '1:1': [1024, 765], '4:3': [1024, 756], '3:2': [1024, 770], '2:1': [1024, 842], '3:1': [1024, 886] },
    R0086: { '3:4': [1024, 624], '2:3': [1024, 644], '1:1': [1024, 610], '4:3': [1024, 555], '3:2': [1024, 602], '2:1': [1024, 622], '3:1': [1024, 652] },
    R0278: { '3:4': [1024, 597], '2:3': [1024, 566], '1:1': [1024, 574], '4:3': [1024, 653], '3:2': [1024, 668], '2:1': [1024, 768], '3:1': [1024, 833] },
    R0294: { '3:4': [1024, 508], '2:3': [1024, 496], '1:1': [1024, 504], '4:3': [1024, 528], '3:2': [1024, 520], '2:1': [1024, 610], '3:1': [1024, 688] },
    R0308: { '3:4': [976, 517], '2:3': [970, 513], '1:1': [976, 530], '4:3': [972, 562], '3:2': [970, 608], '2:1': [972, 666], '3:1': [973, 774] },
    R0310: { '1:1': [986, 470], '4:3': [983, 478], '3:2': [986, 480], '2:1': [988, 498], '3:1': [985, 562] },
    R0302: { '3:4': [1024, 597], '2:3': [1024, 593], '1:1': [1024, 576], '4:3': [1024, 574], '3:2': [1024, 578], '2:1': [1036, 634], '3:1': [1038, 695] },
    R0264: { '4:3': [1024, 400], '3:2': [1024, 394], '2:1': [1024, 392], '3:1': [1024, 390] },
    R0424: { '4:3': [1024, 472], '3:2': [1024, 460], '2:1': [1024, 464], '3:1': [1024, 481] },
    R0155: { '3:4': [759, 670], '2:3': [759, 659], '1:1': [759, 650], '4:3': [756, 781], '3:2': [708, 728], '2:1': [710, 830], '3:1': [691, 907] },
    R0226: { '3:4': [984, 764], '2:3': [984, 721], '1:1': [984, 782], '4:3': [1024, 606], '3:2': [1024, 616], '2:1': [1024, 584], '3:1': [1024, 584] },
    R0457: { '3:4': [1024, 538], '2:3': [1024, 545], '1:1': [1024, 556], '4:3': [1024, 578], '3:2': [1024, 581], '2:1': [967, 718], '3:1': [868, 758] },
    R0222: { '3:4': [994, 544], '2:3': [1004, 522], '1:1': [1024, 536], '4:3': [1002, 574], '3:2': [1006, 594], '2:1': [1024, 516], '3:1': [1024, 628] },
    R0196: { '3:4': [1024, 532], '2:3': [1024, 536], '1:1': [1024, 545], '4:3': [1024, 544], '3:2': [1024, 556], '2:1': [1024, 590], '3:1': [1024, 482] },
    R0188: { '3:4': [752, 636], '2:3': [752, 610], '1:1': [743, 679], '4:3': [1024, 596], '3:2': [1024, 608], '2:1': [1024, 577], '3:1': [1024, 577] },
  };
  for (const id in ROOMS) ROOMS[id].shapes = SHAPES[id] || {};

  /* The shape of a size: the nearest of the catalog's ratios */
  const RATIOS = { '3:4': 3 / 4, '2:3': 2 / 3, '1:1': 1, '4:3': 4 / 3, '3:2': 3 / 2, '2:1': 2, '3:1': 3 };
  const ratioOf = (sz) => {
    const a = sz.w / sz.h;
    return Object.keys(RATIOS).reduce((best, r) => (Math.abs(Math.log(RATIOS[r] / a)) < Math.abs(Math.log(RATIOS[best] / a)) ? r : best));
  };

  /* The catalog's colour rule (catalog-engine/src/rooms/taxonomy.ts + selection.ts, as is):
     a design colour -> the room palettes that show it; neutral palettes suit any artwork. */
  const COLOUR_PALETTES = {
    blue: ['Blue'], turquoise: ['Blue'], teal: ['Blue'],
    green: ['Green'],
    white: ['Light Neutral'], beige: ['Light Neutral'],
    grey: ['Gray'],
    black: ['Dark Neutral'], 'black & white': ['B&W'],
    brown: ['Earthy'],
    yellow: ['Yellow'], gold: ['Yellow'],
    orange: ['Orange'], red: ['Red'], pink: ['Pink'], purple: ['Purple'],
    colorful: ['Colorful', 'Pop of Color'],
  };
  const NEUTRAL_PALETTES = new Set(['Light Neutral', 'Gray', 'Earthy', 'B&W']);
  const DARK_COLOURS = new Set(['black', 'grey', 'gray', 'brown', 'black & white']);
  /* The storefront's colour swatch names -> the catalog's colour tags */
  const COLOUR_ALIASES = { multicolor: 'colorful', gray: 'grey' };

  /* The room palettes a design's colours ask for, strongest colour first (colourTargets) */
  const colourTargets = (colors) => {
    const out = [];
    for (const c of colors.slice(0, 4)) {
      const palettes = COLOUR_PALETTES[c] || [];
      if (!palettes.length || palettes.every((p) => out.some((t) => t.palettes.includes(p)))) continue;
      out.push({ colour: c, palettes });
    }
    return out;
  };
  /* A room's palette should not fight the artwork (paletteFits) */
  const paletteFits = (palette, colors) => {
    const dominant = colors[0] || '';
    switch (palette) {
      case 'Dark Neutral': return colors.some((c) => DARK_COLOURS.has(c));
      case 'Pink': case 'Purple': return colors.includes('pink') || colors.includes('purple');
      case 'Blue': case 'Green': return dominant !== 'orange' && dominant !== 'red';
      case 'Orange': case 'Yellow': case 'Red': return dominant !== 'blue' && dominant !== 'turquoise' && dominant !== 'teal';
      default: return true;
    }
  };
  /* [tier, strongest matching colour]: 0 = in the design's colours, 1 = neutral, 2 = anything
     else that does not fight it; null = it fights it. A design without colour data gets the
     neutral rooms first. */
  const colourRank = (room, colors, targets) => {
    if (!targets.length) return [room.palettes.some((p) => NEUTRAL_PALETTES.has(p)) ? 0 : 1, 0];
    const match = targets.findIndex((t) => t.palettes.some((p) => room.palettes.includes(p)));
    if (match >= 0) return [0, match];
    if (!room.palettes.some((p) => paletteFits(p, colors))) return null;
    return [room.palettes.some((p) => NEUTRAL_PALETTES.has(p)) ? 1 : 2, 0];
  };

  /* Same geometry as the AR preview (snippets/room-preview.liquid) */
  const FRAME_FACE_IN = 0.75, FRAME_GAP_IN = 0.25, PANEL_GAP_IN = 1.5;
  /* Clear of the furniture under it by 4-8in (to 15in to clear a lamp or vase on it) */
  const MIN_GAP_IN = 4, MAX_GAP_IN = 8, MAX_GAP_DECOR_IN = 15;
    const TOP_MARGIN_PX = 82;
  /* Art width against the furniture under it. Portrait pieces are narrow by nature. */
  const SHARE = {
    landscape: { target: 0.68, ideal: [0.58, 0.82], ok: [0.5, 0.9] },
    portrait: { target: 0.58, ideal: [0.42, 0.8], ok: [0.4, 0.9] },
  };
  /* The art fills at least this much of the view; rooms shot from far away zoom in */
  const MIN_FILL = 0.42, MAX_ZOOM = 1.6;
  const TONES = {
    black: { face: '#232323', light: '#3a3a3a', dark: '#0e0e0e' },
    natural: { face: '#c9a88f', light: '#dcc1ab', dark: '#a6866e' },
    walnut: { face: '#4a3128', light: '#6a4a3c', dark: '#2b1a14' },
  };

  /* "68x34 | 22x34 ea." -> { w: 68, h: 34, panels: 3, pw: 22 } */
  const parseSize = (value) => {
    const [whole, each] = String(value).split('|').map((t) => t.trim());
    const [w, h] = whole.toLowerCase().split('x').map(parseFloat);
    if (!(w > 0 && h > 0)) return null;
    if (!each) return { w, h, panels: 1, pw: w };
    const pw = parseFloat(each);
    return { w, h, panels: Math.max(2, Math.round(w / pw)), pw };
  };
  const wholeOf = (value) => String(value).split('|')[0].trim();
  const frameKind = (value) => {
    const f = String(value || '').toLowerCase();
    return ['black', 'natural', 'walnut', 'rolled'].find((k) => f.includes(k)) || 'wrapped';
  };
  const footprint = (sz, kind) => {
    const border = sz.panels === 1 && kind in TONES ? 2 * (FRAME_FACE_IN + FRAME_GAP_IN) : 0;
    const w = sz.panels > 1 ? sz.panels * sz.pw + (sz.panels - 1) * PANEL_GAP_IN : sz.w + border;
    return { w, h: sz.h + border };
  };
  const hits = (a, b) => a[0] < b[2] && a[2] > b[0] && a[1] < b[3] && a[3] > b[1];

  /* Where the art hangs in a room, or null when the room is not approved for its shape or it
     does not fit. Centred where the console places that shape; then kept 4-12in above the
     furniture (a small piece does not float at a centre chosen for a bigger one), on the clear
     wall and clear of lamps and plants. */
  const placeIn = (room, fp, ratio) => {
    const anchor = room.shapes[ratio];
    if (!anchor) return null;
    const w = fp.w * room.ppi, h = fp.h * room.ppi;
    const [left, top, right] = room.wall;
    if (w > right - left) return null;
    // over the furniture whose size the caption names: where the console centres a shape off it
    // (on the open wall beside a sideboard), or the piece is much narrower than the furniture
    // (the console tuned its spot for a bigger one), centre on the furniture instead
    const offFurniture = Math.abs(anchor[0] - room.cx) > 0.25 * room.furnIn * room.ppi;
    const ax = offFurniture || fp.w < 0.6 * room.furnIn ? room.cx : anchor[0];
    const x = Math.min(Math.max(ax - w / 2, left), right - w);
    const lowest = room.topY - MIN_GAP_IN * room.ppi - h;  // y of the top edge at the smallest gap
    const highest = top + TOP_MARGIN_PX;
    const blocked = (y) => (room.avoid || []).some((r) => hits([x, y, x + w, y + h], r));
    let y = Math.min(Math.max(anchor[1] - h / 2, room.topY - MAX_GAP_IN * room.ppi - h), lowest);
    if (y < highest) return null;
    // step up past a lamp or vase, as far as the decor allowance
    const ceiling = Math.max(highest, room.topY - MAX_GAP_DECOR_IN * room.ppi - h);
    while (blocked(y) && y - room.ppi / 2 >= ceiling) y -= room.ppi / 2;
    if (blocked(y)) return null;
    return { x, y, w, h };
  };

  /* One room per size, decided over the whole ladder (ascending). Among the rooms whose
     furniture suits the size: the ideal proportion, then the best colour tier, then the
     strongest of the design's colours, then the share nearest the target. The previous
     size's room is kept while it is as good on colour and still ideal, so the painting
     visibly grows. */
  const assignRooms = (sizes, kind, available, colors) => {
    const targets = colourTargets(colors);
    const wholes = [...new Set(sizes.map(wholeOf))].filter(parseSize).sort((a, b) => parseSize(a).w - parseSize(b).w);
    const map = {};
    let prev = null;
    for (const whole of wholes) {
      const sz = parseSize(whole), fp = footprint(sz, kind), ratio = ratioOf(sz);
      // its splits ("60x40 | 20x40 ea.") hang wider than the single canvas (the gaps between panels): the room
      // must take every version, so switching between them keeps the room and the camera still
      const versions = sizes.filter((s) => wholeOf(s) === whole).map((s) => footprint(parseSize(s), kind));
      const takesAll = (id) => versions.every((v) => placeIn(ROOMS[id], v, ratio));
      const rule = sz.w < sz.h ? SHARE.portrait : SHARE.landscape;
      const inside = ([lo, hi], share) => share >= lo && share <= hi;
      let fits = available.map((id) => {
        const share = sz.w / ROOMS[id].furnIn;
        return { id, share, at: placeIn(ROOMS[id], fp, ratio) && takesAll(id), colour: colourRank(ROOMS[id], colors, targets), ideal: inside(rule.ideal, share) ? 0 : 1 };
      }).filter((c) => c.at && c.colour && inside(rule.ok, c.share));
      if (!fits.length) {
        // nothing suits it on both counts: proportion first, colour second
        fits = available.map((id) => ({ id, share: sz.w / ROOMS[id].furnIn, at: placeIn(ROOMS[id], fp, ratio) && takesAll(id), colour: colourRank(ROOMS[id], colors, targets) || [3, 0], ideal: 1 }))
          .filter((c) => c.at);
      }
      // proportion first (a small piece lost over big furniture is the one thing to avoid), colour next
      const key = (c) => [c.ideal, c.colour[0], c.colour[1], Math.abs(c.share - rule.target)];
      const cmp = (a, b) => { const x = key(a), y = key(b); for (let i = 0; i < x.length; i++) if (x[i] !== y[i]) return x[i] - y[i]; return 0; };
      fits.sort(cmp);
      const best = fits[0];
      const keep = best && prev && fits.find((c) => c.id === prev && c.colour[0] === best.colour[0] && c.ideal === 0 && best.ideal === 0);
      prev = map[whole] = (keep || best)?.id || null;
    }
    return (value) => map[wholeOf(value)] || null;
  };

  /* The square of the photo a room is shown through: every size that hangs in it plus the
     whole furniture, zoomed in (up to MAX_ZOOM) when the art would be small in the frame.
     One view per room, so switching between its sizes keeps the camera still and only the
     painting grows or shrinks (zooming as well would hide the very change being shown). */
  const viewFor = (room, boxes) => {
    const fw = room.furnIn * room.ppi;
    const art = boxes.reduce((u, b) => [Math.min(u[0], b.x), Math.min(u[1], b.y), Math.max(u[2], b.x + b.w), Math.max(u[3], b.y + b.h)], [Infinity, Infinity, -Infinity, -Infinity]);
    const smallest = Math.min(...boxes.map((b) => Math.max(b.w, b.h)));
    const box = [Math.min(art[0], room.cx - fw / 2), art[1], Math.max(art[2], room.cx + fw / 2), room.floorY];
    let S = Math.max(Math.max(box[2] - box[0], box[3] - box[1]) * 1.12, smallest / MIN_FILL, 2048 / MAX_ZOOM);
    if (S > 1900) S = 2048;
    const x0 = Math.min(Math.max((box[0] + box[2]) / 2 - S / 2, 0), 2048 - S);
    const y0 = Math.min(Math.max((box[1] + box[3]) / 2 - S / 2, 0), 2048 - S);
    return { x0, y0, S };
  };

  const pct = (v) => `${(v / 20.48).toFixed(4)}%`; // 2048-unit room coordinate -> % of the room
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const MOVE_MS = 650;

  /* The pieces of the artwork (one canvas or frame, or the panels of a split) as markup
     relative to the art box. `key` changes when the structure does (frame kind / panels). */
  const artMarkup = (sz, kind, fp, artSrc) => {
    const framed = sz.panels === 1 && kind in TONES, tone = TONES[kind];
    const inset = framed ? FRAME_FACE_IN : 0, gap = framed ? FRAME_GAP_IN : 0;
    const pieces = sz.panels > 1
      ? Array.from({ length: sz.panels }, (_, i) => ({ x: i * (sz.pw + PANEL_GAP_IN), w: sz.pw, slice: i }))
      : [{ x: 0, w: fp.w, slice: 0 }];
    const html = pieces.map((p) => {
      const ix = ((inset + gap) / p.w) * 100, iy = ((inset + gap) / fp.h) * 100;
      const face = framed
        ? `<span class="room-sizer__face" style="--face:${tone.face};--face-light:${tone.light};--face-dark:${tone.dark}"></span>
           <span class="room-sizer__reveal" style="inset:${(inset / fp.h) * 100}% ${(inset / p.w) * 100}%"></span>`
        : '';
      const bg = sz.panels > 1
        ? `background-size:${sz.panels * 100}% 100%;background-position:${(p.slice / (sz.panels - 1)) * 100}% 0`
        : 'background-size:100% 100%';
      return `<span class="room-sizer__piece room-sizer__piece--${framed ? 'framed' : kind}" style="left:${(p.x / fp.w) * 100}%;width:${(p.w / fp.w) * 100}%">${face}`
        + `<span class="room-sizer__canvas" style="inset:${iy}% ${ix}%;background-image:url('${artSrc}');${bg}"></span></span>`;
    }).join('');
    return { key: `${framed ? 'framed-' + kind : kind}|${sz.panels}`, html };
  };

  /* Copy the geometry of freshly built pieces onto existing ones, so a size change within the
     same structure animates (CSS transitions on left/width/inset) instead of snapping */
  const morphPieces = (inner, html) => {
    const tpl = document.createElement('span');
    tpl.innerHTML = html;
    const next = tpl.querySelectorAll('.room-sizer__piece, .room-sizer__reveal, .room-sizer__canvas');
    const cur = inner.querySelectorAll('.room-sizer__piece, .room-sizer__reveal, .room-sizer__canvas');
    if (next.length !== cur.length) return false;
    cur.forEach((el, i) => el.setAttribute('style', next[i].getAttribute('style')));
    return true;
  };

  /* A room scene: the photo and the art box, both in the room's 2048 coordinates inside a
     "world" that the view scales and shifts into the square */
  const buildScene = (id, roomSrc, v) => {
    const scene = document.createElement('div');
    scene.className = 'room-sizer__scene';
    scene.dataset.room = id;
    const z = 2048 / v.S;
    scene.innerHTML = `<div class="room-sizer__world" style="transform:scale(${z}) translate(${-pct(v.x0).slice(0, -1)}%, ${-pct(v.y0).slice(0, -1)}%)">`
      + `<img class="room-sizer__room" src="${roomSrc}" alt="" draggable="false" decoding="async">`
      + `<span class="room-sizer__art"><span class="room-sizer__art-inner"></span><span class="room-sizer__light" style="background-image:url('${roomSrc}')"></span></span></div>`;
    return scene;
  };

  const placeArt = (scene, at) => {
    const art = scene.querySelector('.room-sizer__art');
    Object.assign(art.style, { left: pct(at.x), top: pct(at.y), width: pct(at.w), height: pct(at.h) });
    // the room's own light (falloff, sun patches) across the art, aligned to the photo
    const light = scene.querySelector('.room-sizer__light');
    Object.assign(light.style, {
      backgroundSize: `${(2048 / at.w) * 100}% ${(2048 / at.h) * 100}%`,
      backgroundPosition: `${(at.x / (2048 - at.w)) * 100}% ${(at.y / (2048 - at.h)) * 100}%`,
    });
  };

  const setArt = (scene, art, animate) => {
    const box = scene.querySelector('.room-sizer__art');
    // a change while the last one is still fading out: that one goes now, so the current art is the only one
    // left (picked "first" while two were there, a quick second change kept the stale one on top - Black,
    // Natural, Walnut in quick succession stayed on Natural)
    box.querySelectorAll('.room-sizer__art-inner.is-leaving').forEach((el) => el.remove());
    const inner = box.querySelector('.room-sizer__art-inner');
    if (inner.dataset.key === art.key && morphPieces(inner, art.html)) return;
    const fresh = document.createElement('span');
    fresh.className = 'room-sizer__art-inner';
    fresh.dataset.key = art.key;
    fresh.innerHTML = art.html;
    if (!animate || !inner.dataset.key) {
      inner.replaceWith(fresh);
      return;
    }
    // another frame or another structure (frame <-> canvas, 1 <-> 3 panels): crossfade the two, the new one on top
    fresh.classList.add('is-hidden');
    box.append(fresh);
    inner.classList.add('is-leaving');
    requestAnimationFrame(() => requestAnimationFrame(() => fresh.classList.remove('is-hidden')));
    setTimeout(() => inner.remove(), MOVE_MS);
  };

  class RoomSizer extends HTMLElement {
    connectedCallback() {
      this.framePosition = parseInt(this.dataset.framePosition, 10) || 0;
      this.sizes = JSON.parse(this.dataset.sizes || '[]');
      // the design's colours, strongest first (shopify.color-pattern), as the catalog's colour tags
      this.colors = JSON.parse(this.dataset.colors || '[]').map((c) => String(c).toLowerCase().trim()).map((c) => COLOUR_ALIASES[c] || c);
      this.urls = JSON.parse(this.querySelector('[data-room-sizer-rooms]')?.textContent || '{}');
      this.available = Object.keys(ROOMS).filter((id) => this.urls[id]);
      this.state = { size: this.dataset.size, frame: this.dataset.frame };
      this.stage = this.querySelector('[data-room-sizer-view]');
      this.ladders = {};
      this.views = {};
      // The room photo (150-400 KB) and the artwork load only once the preview first opens (show()).
      this.awake = false;
      new ResizeObserver(() => {
        this.style.setProperty('--k', this.clientWidth / 2048);
        if (this.awake && !this.scene) this.render();
      }).observe(this);
    }

    // show this size and frame - the painting glides to it
    show(size, frame) {
      this.state = { size, frame: this.framePosition ? (frame || '') : '' };
      if (!this.awake) {
        this.awake = true;
        return this.render();
      }
      this.render({ animate: true });
    }

    // ...and the rooms it may go to for the sizes it can show, ready before they're asked for
    preloadFor(frame) {
      if (!this.clientWidth) return;
      const kind = frameKind(frame);
      this.preloadedKinds ||= {};
      if (this.preloadedKinds[kind]) return;
      this.preloadedKinds[kind] = true;
      new Set(this.sizes.map((s) => this.roomFor(s, kind)).filter(Boolean)).forEach((id) => {
        const img = new Image();
        img.fetchPriority = 'low';
        img.src = this.pickUrl(id, this.viewOf(id, kind));
      });
    }

    roomFor(size, kind) {
      this.ladders[kind] ||= assignRooms(this.sizes, kind, this.available, this.colors);
      return this.ladders[kind](size);
    }

    /* The fixed view of a room: framed on every size that hangs in it (for this frame kind) */
    viewOf(id, kind) {
      const key = `${id}|${kind}`;
      if (!this.views[key]) {
        const boxes = this.sizes.filter((s) => this.roomFor(s, kind) === id)
          .map((s) => placeIn(ROOMS[id], footprint(parseSize(s), kind), ratioOf(parseSize(s)))).filter(Boolean);
        this.views[key] = viewFor(ROOMS[id], boxes);
      }
      return this.views[key];
    }

    /* urls[id] = [1024w, 1600w, 2048w] */
    pickUrl(id, v) {
      const need = this.clientWidth * (window.devicePixelRatio || 1) * (2048 / v.S);
      const i = [1024, 1600, 2048].findIndex((w) => w >= need);
      return this.urls[id][i < 0 ? 2 : i];
    }

    artSrc(widthPx) {
      const w = [300, 600, 900, 1200, 1600].find((x) => x >= widthPx) || 1600;
      return this.dataset.art.replace(/width=\d+/, `width=${w}`);
    }

    render({ animate = false } = {}) {
      const sz = parseSize(this.state.size);
      const kind = frameKind(this.state.frame);
      let id = sz && this.roomFor(this.state.size, kind);
      const W = this.clientWidth;
      if (!id || !W) return;
      const fp = footprint(sz, kind), ratio = ratioOf(sz);
      let at = placeIn(ROOMS[id], fp, ratio);
      let v;
      if (at) {
        v = this.viewOf(id, kind);
      } else {
        // no room on the ladder takes this version of the size: any room that does, framed on it alone
        id = this.available.find((rid) => placeIn(ROOMS[rid], fp, ratio)) || null;
        if (!id) return;
        at = placeIn(ROOMS[id], fp, ratio);
        v = viewFor(ROOMS[id], [at]);
      }
      this.style.setProperty('--k', W / 2048);
      const dpr = window.devicePixelRatio || 1;
      const art = artMarkup(sz, kind, fp, this.artSrc((at.w / v.S) * W * dpr));
      animate = animate && !!this.scene && !reduceMotion();
      this.finishTransition?.();
      this.dataset.room = id;

      if (!this.awake) return; // not opened yet: no photo to fetch

      if (this.scene?.dataset.room === id) {
        // Same room: the camera stays, the painting grows / shrinks in place
        this.classList.toggle('is-animating', animate);
        placeArt(this.scene, at);
        setArt(this.scene, art, animate);
      } else {
        const incoming = buildScene(id, this.pickUrl(id, v), v);
        placeArt(incoming, at);
        setArt(incoming, art, false);
        if (!animate) {
          const first = !this.scene;
          this.stage.replaceChildren(incoming);
          if (first) {
            // fade the first room in once its photo can paint, rather than popping in half-loaded
            incoming.classList.add('is-entering');
            const img = incoming.querySelector('.room-sizer__room');
            Promise.race([img.decode?.() || Promise.resolve(), new Promise((r) => setTimeout(r, 1500))]).catch(() => {})
              .then(() => requestAnimationFrame(() => incoming.classList.remove('is-entering')));
          }
        } else {
          this.crossfade(this.scene, incoming, this.current, { at, v }, art);
        }
        this.scene = incoming;
      }
      this.current = { at, v };
    }

    /* Another room: the rooms crossfade while the painting glides from its old size and
       place to the new one - the one continuous thing on screen */
    crossfade(outgoing, incoming, from, to, art) {
      const W = this.clientWidth;
      const rect = ({ at, v }) => ({
        left: `${((at.x - v.x0) / v.S) * 100}%`, top: `${((at.y - v.y0) / v.S) * 100}%`,
        width: `${(at.w / v.S) * 100}%`, height: `${(at.h / v.S) * 100}%`,
      });
      const flyer = document.createElement('span');
      flyer.className = 'room-sizer__flyer';
      flyer.style.setProperty('--k', W / to.v.S);
      flyer.innerHTML = `<span class="room-sizer__art-inner">${art.html}</span>`;
      flyer.append(incoming.querySelector('.room-sizer__light').cloneNode());
      Object.assign(flyer.style, rect(from));

      incoming.classList.add('is-entering', 'is-art-hidden');
      outgoing.classList.add('is-art-hidden');
      this.stage.append(incoming, flyer);

      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        this.finishTransition = null;
        outgoing.remove();
        flyer.remove();
        incoming.classList.remove('is-entering', 'is-art-hidden');
      };
      this.finishTransition = finish;

      const start = () => {
        if (done) return;
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (done) return;
          outgoing.classList.add('is-leaving');
          incoming.classList.remove('is-entering');
          Object.assign(flyer.style, rect(to));
          setTimeout(finish, MOVE_MS + 40);
        }));
      };
      // Start once the new photo can paint (never wait more than a moment for it)
      const img = incoming.querySelector('.room-sizer__room');
      Promise.race([img.decode?.() || Promise.resolve(), new Promise((r) => setTimeout(r, 900))]).catch(() => {}).then(start);
    }
  }

  window.customElements.define('room-sizer', RoomSizer);
})();
